// src/components/document/DocumentChronicleView.tsx
// Chronicle — the document flow told as a daily feed. Events come from three
// real sources, merged: the activity service (tenant stream, client-filtered
// to document objects), workflow pending decisions (approvals awaiting an
// actor) and the documents list itself (derived status facts). The rail
// carries the control surface: expiring reviews, acknowledgment progress per
// document and the user's drafts — on narrow screens the same sections stack
// under the feed instead of disappearing.

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';

import { ListActivities } from '@core/api/endpoints/activity/list_activities';
import { ListPendingDecisions } from '@core/api/endpoints/workflow/list_pending_decisions';
import { ListAcknowledgments } from '@core/api/endpoints/document/list_acknowledgments';
import { GetDocumentStats } from '@core/api/endpoints/document/get_document_stats';
import type { Activity } from '@core/types/activity';
import type { PendingDecision } from '@core/types/workflow';
import type { DocumentStatsResponse } from '@core/types/document';

import {
  activityToEvents,
  decisionsToEvents,
  deadlineDaysLabel,
  deadlineTone,
  deriveDocumentEvents,
  documentsExpiring,
  documentIdOf,
  filterEvents,
  groupEventsByDay,
  mergeEvents,
  toMs,
  type ChronicleEvent,
  type ChronicleEventKind,
  type ChronicleFilterId,
} from '@/features/document/chronicleLogic';
import { formatRelativeTime } from '@/features/document/logic';
import { EntityAvatar } from '@/components/EntityAvatar';

// ---------------------------------------------------------------------------
// Event dot — the kind classifier reads by color, hollow = machine-generated
// ---------------------------------------------------------------------------

const KIND_DOT: Record<ChronicleEventKind, string> = {
  published: 'bg-primary',
  approval: 'bg-warning',
  signature: 'bg-success',
  generated: 'border-2 border-primary bg-background',
  dispute: 'bg-destructive',
  reminder: 'bg-muted-foreground/60',
  expiring: 'bg-warning',
  created: 'bg-muted-foreground/60',
  updated: 'bg-muted-foreground/60',
};

/** Action badges name the event's nature in the product's own words. */
const KIND_BADGE: Partial<Record<ChronicleEventKind, { labelKey: string; className: string }>> = {
  dispute: { labelKey: 'document.chronicle.badge.dispute', className: 'bg-destructive/10 text-destructive' },
  signature: { labelKey: 'document.chronicle.badge.signature', className: 'bg-success/10 text-success' },
  generated: { labelKey: 'document.chronicle.badge.generated', className: 'bg-primary/10 text-foreground' },
  published: { labelKey: 'document.chronicle.badge.published', className: 'bg-primary/10 text-foreground' },
};

function EventDot({ kind }: { kind: ChronicleEventKind }) {
  return <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${KIND_DOT[kind] ?? 'bg-muted-foreground/60'}`} />;
}

function ActorAvatar({ name, image, userId }: { name: string; image?: string; userId?: string }) {
  return <EntityAvatar name={name} image={image} userId={userId} size="xs" className="mt-0.5 h-[18px] w-[18px] text-[9px]" />;
}

// ---------------------------------------------------------------------------
// Acknowledgment progress per document — the rail answers "who hasn't signed",
// not just "how many". Sources are per-target, so the freshest published
// documents are probed; documents with nothing pending drop out.
// ---------------------------------------------------------------------------

interface AckRow {
  docId: string;
  title: string;
  number?: string;
  acked: number;
  total: number;
}

function useAckRows(documents: any[]) {
  const [rows, setRows] = useState<AckRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const candidates = documents
      .filter((d: any) => String(d?.status ?? '').replace('DOCUMENT_STATUS_', '') === 'PUBLISHED')
      .sort((a: any, b: any) => toMs(b.updated_at) - toMs(a.updated_at))
      .slice(0, 4)
      .map((doc: any) => ({ ...doc, __id: documentIdOf(doc) ?? '' }))
      .filter((doc: any) => doc.__id);

    Promise.all(
      candidates.map((doc: any) =>
        ListAcknowledgments('document', doc.__id)
          .then((res) => {
            const items = res?.acknowledgments?.acknowledgments ?? [];
            const acked = items.filter((a: any) => String(a?.status ?? '').toUpperCase().includes('ACKNOWLEDGED')).length;
            return {
              docId: doc.__id,
              title: doc.title || '',
              number: doc.document_number || undefined,
              acked,
              total: items.length,
            } as AckRow;
          })
          .catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      setRows((results.filter(Boolean) as AckRow[]).filter((row) => row.total > 0 && row.acked < row.total));
    });
    return () => { cancelled = true; };
  }, [documents]);

  return rows;
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

interface DocumentChronicleViewProps {
  documents: any[];
  onOpen: (id: string) => void;
}

const FILTERS: ChronicleFilterId[] = ['all', 'approvals', 'signatures', 'templates', 'disputes', 'deadlines'];

export const DocumentChronicleView = observer(({ documents, onOpen }: DocumentChronicleViewProps) => {
  const { t, locale } = useI18n();
  const [filter, setFilter] = useState<ChronicleFilterId>('all');
  const [activities, setActivities] = useState<Activity[] | null>(null);
  const [decisions, setDecisions] = useState<PendingDecision[] | null>(null);
  const [stats, setStats] = useState<DocumentStatsResponse['stats'] | null>(null);
  const [degradedSources, setDegradedSources] = useState<string[]>([]);

  const markDegraded = (key: string) => setDegradedSources((prev) => (prev.includes(key) ? prev : [...prev, key]));

  useEffect(() => {
    let cancelled = false;
    // The chronicle is a window, not an archive: one tenant page of events,
    // filtered to documents client-side (REST filter has no object_type yet).
    ListActivities({ scope: 'ACTIVITY_SCOPE_TENANT', limit: 100 })
      .then((res) => { if (!cancelled) setActivities(res.activities ?? []); })
      .catch(() => { if (!cancelled) { setActivities([]); markDegraded('activity'); } });
    ListPendingDecisions()
      .then((res) => { if (!cancelled) setDecisions(res.items ?? []); })
      .catch(() => { if (!cancelled) { setDecisions([]); markDegraded('decisions'); } });
    GetDocumentStats()
      .then((res) => { if (!cancelled) setStats(res.stats ?? null); })
      .catch(() => { if (!cancelled) { setStats(null); markDegraded('stats'); } });
    return () => { cancelled = true; };
  }, []);

  const events: ChronicleEvent[] = useMemo(
    () => mergeEvents(
      activityToEvents(activities, t),
      decisionsToEvents(decisions, t),
      deriveDocumentEvents(documents, t),
    ),
    [activities, decisions, documents, t],
  );

  const filtered = useMemo(() => filterEvents(events, filter), [events, filter]);
  const dayGroups = useMemo(
    () => groupEventsByDay(filtered, locale, { today: t('document.chronicle.day.today'), yesterday: t('document.chronicle.day.yesterday') }),
    [filtered, locale, t],
  );

  const expiring = useMemo(() => documentsExpiring(documents).slice(0, 5), [documents]);
  const drafts = useMemo(
    () => documents
      .filter((d: any) => String(d?.status ?? '').toLowerCase().replace('document_status_', '') === 'draft')
      .sort((a: any, b: any) => toMs(b.updated_at) - toMs(a.updated_at))
      .slice(0, 4),
    [documents],
  );
  const ackRows = useAckRows(documents);

  const timeFmt = useMemo(() => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }), [locale]);

  const ackPending = stats?.pending_acknowledgments ?? 0;
  const ackOverdue = stats?.overdue_acknowledgments ?? 0;

  const degradedMessage = degradedSources.length > 0
    ? `${t('document.chronicle.degraded')}: ${degradedSources.map((key) => t(`document.chronicle.source.${key}`)).join(', ')}`
    : null;

  const renderRailSections = (stacked: boolean) => (
    <>
      {/* Expiring */}
      <section className={stacked ? '' : undefined}>
        <h3 className="mb-2 text-xs font-medium text-muted-foreground">{t('document.chronicle.rail.expiring')}</h3>
        <div className="rounded-lg border border-border">
          {expiring.length === 0 && (
            <p className="px-3 py-4 text-xs text-muted-foreground">{t('document.chronicle.rail.noExpiring')}</p>
          )}
          {expiring.map((item) => {
            const tone = deadlineTone(item.reviewMs);
            return (
              <button
                key={item.id}
                onClick={() => onOpen(item.id)}
                className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted/50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[13px] text-foreground">{item.title || t('common.untitled')}</span>
                  {item.number && <span className="block text-xs tabular-nums text-muted-foreground">{item.number}</span>}
                </span>
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium tabular-nums ${
                    tone === 'red'
                      ? 'bg-destructive/10 text-destructive'
                      : tone === 'amber'
                        ? 'bg-warning/15 text-warning'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {deadlineDaysLabel(item.reviewMs, locale)}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Acknowledgments — per-document progress, the "who hasn't signed" view */}
      <section>
        <h3 className="mb-2 text-xs font-medium text-muted-foreground">{t('document.chronicle.rail.acks')}</h3>
        <div className="space-y-3 rounded-lg border border-border p-3">
          {ackRows && ackRows.length > 0 && ackRows.map((row) => (
            <button key={row.docId} onClick={() => onOpen(row.docId)} className="block w-full text-left">
              <span className="mb-1 flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate text-[13px] text-foreground">{row.title || t('common.untitled')}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{row.acked}/{row.total}</span>
              </span>
              <span className="block h-[5px] overflow-hidden rounded bg-muted">
                <span
                  className="block h-full rounded bg-success"
                  style={{ width: `${Math.min(100, Math.round((row.acked / row.total) * 100))}%` }}
                />
              </span>
            </button>
          ))}
          {ackRows && ackRows.length === 0 && (
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-semibold tabular-nums text-foreground">{ackPending}</span>
              <span className="text-xs text-muted-foreground">{t('document.chronicle.rail.acksPending')}</span>
            </div>
          )}
          {!ackRows && (
            <p className="text-xs text-muted-foreground">{t('document.chronicle.rail.acksUnavailable')}</p>
          )}
          {ackOverdue > 0 && (
            <p className="text-xs text-destructive">{ackOverdue} {t('document.chronicle.rail.acksOverdue')}</p>
          )}
        </div>
      </section>

      {/* Drafts */}
      <section>
        <h3 className="mb-2 text-xs font-medium text-muted-foreground">{t('document.chronicle.rail.drafts')}</h3>
        <div className="rounded-lg border border-border">
          {drafts.length === 0 && (
            <p className="px-3 py-4 text-xs text-muted-foreground">{t('document.chronicle.rail.noDrafts')}</p>
          )}
          {drafts.map((doc: any) => {
            const id = documentIdOf(doc);
            return (
              <button
                key={id}
                onClick={() => id && onOpen(id)}
                className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted/50"
              >
                <span className="truncate text-[13px] text-foreground">{doc.title || t('common.untitled')}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {formatRelativeTime(doc.updated_at as string, locale)}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </>
  );

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      {/* Feed column */}
      <div className="min-w-0 flex-1">
        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-1.5 pb-4">
          {FILTERS.map((id) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                filter === id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`document.chronicle.filter.${id}`)}
            </button>
          ))}
          <span className="ml-auto text-xs tabular-nums text-muted-foreground">
            {t('document.chronicle.eventCount')}: {filtered.length}
          </span>
        </div>

        {degradedMessage && <ErrorAlert error={degradedMessage} dismissible onDismiss={() => setDegradedSources([])} />}

        {/* Skeleton */}
        {documents.length === 0 && activities === null && (
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex animate-pulse gap-3">
                <div className="h-3 w-10 rounded bg-muted" />
                <div className="mt-2 h-2 w-2 rounded-full bg-muted" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 w-3/4 rounded bg-muted" />
                  <div className="h-3 w-1/2 rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {activities !== null && filtered.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <p className="text-sm font-medium text-foreground">{t('document.chronicle.empty.title')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('document.chronicle.empty.description')}</p>
          </div>
        )}

        {/* Day groups */}
        {dayGroups.map((group) => (
          <section key={group.key} className="mb-2">
            <h3 className="py-2 text-xs font-medium text-muted-foreground">{group.label}</h3>
            <div className="divide-y divide-border">
              {group.events.map((event) => {
                const badge = KIND_BADGE[event.kind];
                return (
                  <button
                    key={event.id}
                    onClick={() => event.documentId && onOpen(event.documentId)}
                    className={`flex w-full gap-3 py-2.5 pr-2 text-left transition-colors ${
                      event.documentId ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'
                    }`}
                  >
                    <span className="w-10 shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground">
                      {timeFmt.format(event.at)}
                    </span>
                    <EventDot kind={event.kind} />
                    {event.actorName && <ActorAvatar name={event.actorName} image={event.actorAvatar} userId={event.actorId} />}
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] leading-snug text-foreground">
                        {badge && (
                          <span className={`mr-1.5 inline-block rounded px-1.5 py-px text-[11px] font-medium ${badge.className}`}>
                            {t(badge.labelKey)}
                          </span>
                        )}
                        {event.title}
                      </span>
                      {event.context && (
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{event.context}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* Right rail — desktop aside; the same sections stack under the feed
          on narrow screens so the control surface never disappears. */}
      <aside className="hidden w-80 shrink-0 space-y-5 lg:block">{renderRailSections(false)}</aside>
      <div className="space-y-5 border-t border-border pt-5 lg:hidden">{renderRailSections(true)}</div>
    </div>
  );
});
